import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateWebhook,
  type CreateWebhookInput,
  CreateWebhookSchema,
  EVENT_TYPES,
  type UpdateWebhook,
  UpdateWebhookSchema,
  WEBHOOK_SECRET_MAX,
  WEBHOOK_SECRET_MIN,
  type WebhookSubscription,
} from '@servicetim/shared';
import { useCreateWebhook, useUpdateWebhook } from '@servicetim/ui/api';
import { Button, Input, Label, Switch, toast } from '@servicetim/ui/components';
import { RefreshCw } from 'lucide-react';
import { useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { CheckboxList, type CheckboxListGroup } from '../common/checkbox-list';
import { FormDialog } from '../common/form-dialog';
import { FormField, fieldA11y } from '../common/form-field';
import { randomToken } from '../common/format';
import { useFormSubmit } from '../common/use-form-submit';

/** Event types grouped by aggregate prefix (`ticket`, `comment`, …). */
export function eventTypeGroups(): CheckboxListGroup[] {
  const byPrefix = new Map<string, string[]>();
  for (const type of EVENT_TYPES) {
    const prefix = type.split('.')[0] ?? type;
    byPrefix.set(prefix, [...(byPrefix.get(prefix) ?? []), type]);
  }
  return [...byPrefix.entries()].map(([prefix, types]) => ({
    title: prefix,
    options: types.map((type) => ({ id: type, label: type })),
  }));
}

/** Editing keeps the stored secret unless a new one is typed. */
const EditSchema = UpdateWebhookSchema.required({
  name: true,
  url: true,
  eventTypes: true,
  enabled: true,
});
type EditInput = z.input<typeof EditSchema>;

const emptyToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

export function WebhookDialog({
  webhook,
  onClose,
}: {
  webhook: WebhookSubscription | null;
  onClose: () => void;
}) {
  return webhook ? (
    <EditWebhookDialog webhook={webhook} onClose={onClose} />
  ) : (
    <CreateWebhookDialog onClose={onClose} />
  );
}

function CreateWebhookDialog({ onClose }: { onClose: () => void }) {
  const id = useId();
  const create = useCreateWebhook();
  const form = useForm<CreateWebhookInput, unknown, CreateWebhook>({
    resolver: zodResolver(CreateWebhookSchema),
    defaultValues: { name: '', url: '', eventTypes: [], secret: randomToken(32), enabled: true },
  });
  const submit = useFormSubmit(form);
  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title="New webhook"
      description="Events are POSTed to the HTTPS URL, signed with the secret (HMAC). Delivery starts with Bolt 8."
      formId={`${id}-form`}
      submitLabel="Create webhook"
      size="lg"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (values) => {
        const created = await submit.run(() => create.mutateAsync(values));
        if (created) {
          toast.success(`Webhook ${created.name} created`);
          onClose();
        }
      })}
    >
      <WebhookFields form={form} id={id} mode="create" />
    </FormDialog>
  );
}

function EditWebhookDialog({
  webhook,
  onClose,
}: {
  webhook: WebhookSubscription;
  onClose: () => void;
}) {
  const id = useId();
  const update = useUpdateWebhook();
  const form = useForm<EditInput, unknown, UpdateWebhook>({
    resolver: zodResolver(EditSchema),
    defaultValues: {
      name: webhook.name,
      url: webhook.url,
      eventTypes: webhook.eventTypes,
      enabled: webhook.enabled,
      secret: undefined,
    },
  });
  const submit = useFormSubmit(form);
  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={`Edit ${webhook.name}`}
      description={`Current secret ${webhook.secret} (masked).`}
      formId={`${id}-form`}
      size="lg"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (patch) => {
        const saved = await submit.run(() => update.mutateAsync({ id: webhook.id, patch }));
        if (saved) {
          toast.success('Webhook updated');
          onClose();
        }
      })}
    >
      <WebhookFields form={form} id={id} mode="edit" />
    </FormDialog>
  );
}

/** Shared body; both forms expose the same field names. */
function WebhookFields<TInput extends CreateWebhookInput | EditInput, TOutput>({
  form,
  id,
  mode,
}: {
  form: ReturnType<typeof useForm<TInput, unknown, TOutput>>;
  id: string;
  mode: 'create' | 'edit';
}) {
  const groups = useMemo(eventTypeGroups, []);
  const { errors } = form.formState as {
    errors: Record<string, { message?: string; root?: { message?: string } } | undefined>;
  };
  const eventsError = errors.eventTypes?.message ?? errors.eventTypes?.root?.message;
  // The two input types share every key; `Path` narrowing across the union needs one cast.
  const f = form as unknown as ReturnType<typeof useForm<EditInput, unknown, UpdateWebhook>>;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
          <Input
            {...f.register('name')}
            {...fieldA11y(`${id}-name`, { error: errors.name })}
            maxLength={80}
          />
        </FormField>
        <FormField
          inline
          label="Enabled"
          htmlFor={`${id}-enabled`}
          description="Disabled subscriptions keep their configuration but receive nothing."
          error={errors.enabled?.message}
        >
          <Controller
            control={f.control}
            name="enabled"
            render={({ field }) => (
              <Switch
                id={`${id}-enabled`}
                checked={field.value ?? true}
                onCheckedChange={field.onChange}
                aria-describedby={`${id}-enabled-description`}
              />
            )}
          />
        </FormField>
      </div>
      <FormField
        label="URL"
        htmlFor={`${id}-url`}
        required
        description="HTTPS only."
        error={errors.url?.message}
      >
        <Input
          type="url"
          placeholder="https://example.com/hooks/servicetim"
          {...f.register('url')}
          {...fieldA11y(`${id}-url`, { description: true, error: errors.url })}
        />
      </FormField>
      <FormField
        label={mode === 'create' ? 'Secret' : 'New secret'}
        htmlFor={`${id}-secret`}
        required={mode === 'create'}
        description={
          mode === 'create'
            ? `${WEBHOOK_SECRET_MIN}–${WEBHOOK_SECRET_MAX} characters. Copy it now; the API returns it masked afterwards.`
            : `Leave empty to keep the current secret; ${WEBHOOK_SECRET_MIN}–${WEBHOOK_SECRET_MAX} characters otherwise.`
        }
        error={errors.secret?.message}
      >
        <div className="flex items-center gap-2">
          <Input
            {...f.register('secret', mode === 'edit' ? { setValueAs: emptyToUndefined } : {})}
            {...fieldA11y(`${id}-secret`, { description: true, error: errors.secret })}
            className="font-mono text-xs"
            autoComplete="off"
            spellCheck={false}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              f.setValue('secret', randomToken(32), { shouldDirty: true, shouldValidate: true })
            }
          >
            <RefreshCw /> Generate
          </Button>
        </div>
      </FormField>
      <div className="space-y-1.5">
        <Label id={`${id}-events-label`} asChild>
          <p>
            Event types{' '}
            <span aria-hidden="true" className="text-red-600 dark:text-red-400">
              *
            </span>
          </p>
        </Label>
        <Controller
          control={f.control}
          name="eventTypes"
          render={({ field }) => (
            <CheckboxList
              id={`${id}-events`}
              aria-labelledby={`${id}-events-label`}
              aria-invalid={Boolean(eventsError)}
              groups={groups}
              columns={3}
              value={field.value ?? []}
              onChange={(next) => field.onChange(next)}
              className="max-h-64 overflow-y-auto rounded-md border border-border p-3"
            />
          )}
        />
        {eventsError ? (
          <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
            {eventsError}
          </p>
        ) : null}
      </div>
    </>
  );
}
